import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './EventDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://saskararecord.com/api';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) + ' WIB';
}

function formatPrice(price) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(price);
}

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API_BASE}/events/${id}`)
      .then((res) => {
        if (mounted) setEvent(res.data.data);
      })
      .catch(() => {
        if (mounted) setNotFound(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return <div className="event-detail__state">Memuat...</div>;
  }

  if (notFound || !event) {
    return (
      <div className="event-detail__state">
        <p>Event tidak ditemukan.</p>
        <Link to="/" className="event-detail__back">← Kembali ke Beranda</Link>
      </div>
    );
  }

  const pageTitle = `${event.title} — Saskara Record`;
  const pageDesc = event.description || `Event ${event.title} dari Saskara Record.`;
  const pageImage = event.poster_url || 'https://saskararecord.com/logo512.png';
  const pageUrl = `https://saskararecord.com/events/${id}`;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={pageImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: event.title,
          description: event.description || undefined,
          startDate: event.event_date || undefined,
          image: event.poster_url || undefined,
          location: event.location ? {
            '@type': 'Place',
            name: event.location,
            address: event.city || undefined,
          } : undefined,
          url: pageUrl,
        })}
      </script>
    <div className="event-detail">
      <nav className="event-detail__nav">
        <Link to="/" className="event-detail__back">← Beranda</Link>
      </nav>

      <div className="event-detail__hero">
        <div className="event-detail__poster">
          {event.poster_url ? (
            <img src={event.poster_url} alt={event.title} />
          ) : (
            <div className="event-detail__poster-fallback" />
          )}
        </div>

        <div className="event-detail__info">
          <span className={`event-detail__badge event-detail__badge--${event.status}`}>
            {event.status === 'upcoming' ? 'Segera' : event.status === 'sold_out' ? 'Sold Out' : 'Selesai'}
          </span>
          <h1 className="event-detail__title">{event.title}</h1>

          <div className="event-detail__meta">
            <div className="event-detail__meta-item">
              <span className="event-detail__meta-label">Tanggal</span>
              <span>{formatDate(event.event_date)}</span>
            </div>
            <div className="event-detail__meta-item">
              <span className="event-detail__meta-label">Waktu</span>
              <span>{formatTime(event.event_date)}</span>
            </div>
            <div className="event-detail__meta-item">
              <span className="event-detail__meta-label">Lokasi</span>
              <span>{event.location}{event.city ? `, ${event.city}` : ''}</span>
            </div>
          </div>

          {event.description && <p className="event-detail__desc">{event.description}</p>}

          {event.artists && event.artists.length > 0 && (
            <div className="event-detail__lineup">
              <span className="event-detail__meta-label">Lineup</span>
              <div className="event-detail__lineup-list">
                {event.artists.map((a) => (
                  <Link to={`/artists/${a.slug}`} key={a.id} className="event-detail__lineup-item">
                    {a.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {event.ticket_types && event.ticket_types.length > 0 && (
        <div className="event-detail__tickets">
          <h2>Tiket</h2>
          <div className="event-detail__ticket-list">
            {event.ticket_types.map((t) => (
              <div className="event-detail__ticket-card" key={t.id}>
                <div>
                  <div className="event-detail__ticket-name">{t.name}</div>
                  {t.description && <div className="event-detail__ticket-desc">{t.description}</div>}
                </div>
                <div className="event-detail__ticket-price">{formatPrice(t.price)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
